// ReportPreviewDrawer — simulated report preview side panel
import type { ReportItem } from "@/mocks/relatorios";
import ReportStatusBadge from "./ReportStatusBadge";
import ReportTypeBadge from "./ReportTypeBadge";

interface Props {
  report: ReportItem;
  onClose: () => void;
}

export default function ReportPreviewDrawer({ report, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white shadow-xl overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-background-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-foreground-900 font-heading">Preview do Relatório</h2>
            <p className="text-xs text-foreground-500 mt-0.5">Visualização simulada</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center transition-colors">
            <i className="ri-close-line text-foreground-500 w-full h-full flex items-center justify-center text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Capa simulada */}
          <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200">
            <div className="w-14 h-14 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-article-line text-primary-500 w-full h-full flex items-center justify-center text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-foreground-900 font-heading mb-2">{report.title}</h3>
            <div className="flex items-center justify-center gap-2 mb-3">
              <ReportTypeBadge type={report.type} />
              <ReportStatusBadge status={report.status} />
            </div>
            <div className="text-sm text-foreground-500 space-y-1">
              <p><strong>Projeto:</strong> {report.projectName}</p>
              <p><strong>Município:</strong> {report.municipality}/{report.uf}</p>
              <p><strong>Período:</strong> {report.period}</p>
              <p><strong>Responsável Técnico:</strong> {report.responsible}</p>
              <p><strong>Formato:</strong> {report.format}</p>
              <p><strong>Gerado em:</strong> {report.generatedAt || "Pendente"}</p>
            </div>
          </div>

          {/* KPIs simulados */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Árvores", value: "847", icon: "ri-tree-line" },
              { label: "Espécies", value: "42", icon: "ri-leaf-line" },
              { label: "CO2e (t)", value: "1.167", icon: "ri-cloud-line" },
            ].map((k) => (
              <div key={k.label} className="bg-white rounded-xl border border-background-200/70 p-3 text-center">
                <i className={`${k.icon} text-primary-400 w-5 h-5 flex items-center justify-center mx-auto mb-1`} />
                <div className="text-lg font-bold text-foreground-900 font-heading">{k.value}</div>
                <div className="text-xs text-foreground-400">{k.label}</div>
              </div>
            ))}
          </div>

          {/* Gráfico simulado */}
          <div className="bg-white rounded-xl border border-background-200/70 p-4">
            <h4 className="text-sm font-semibold text-foreground-900 mb-3">Distribuição por Estado Fitossanitário</h4>
            <div className="space-y-2">
              <div>
                <div className="flex justify-between text-xs text-foreground-500 mb-1"><span>Bom</span><span>68%</span></div>
                <div className="h-2 bg-background-100 rounded-full overflow-hidden"><div className="h-full bg-primary-500 rounded-full" style={{ width: "68%" }} /></div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-foreground-500 mb-1"><span>Regular</span><span>22%</span></div>
                <div className="h-2 bg-background-100 rounded-full overflow-hidden"><div className="h-full bg-yellow-500 rounded-full" style={{ width: "22%" }} /></div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-foreground-500 mb-1"><span>Ruim</span><span>8%</span></div>
                <div className="h-2 bg-background-100 rounded-full overflow-hidden"><div className="h-full bg-orange-500 rounded-full" style={{ width: "8%" }} /></div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-foreground-500 mb-1"><span>Morta</span><span>2%</span></div>
                <div className="h-2 bg-background-100 rounded-full overflow-hidden"><div className="h-full bg-red-500 rounded-full" style={{ width: "2%" }} /></div>
              </div>
            </div>
          </div>

          {/* Tabela simulada */}
          <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
            <h4 className="text-sm font-semibold text-foreground-900 p-4 pb-2">Top Espécies</h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 bg-background-50/50">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-foreground-500">Espécie</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-foreground-500">Qtd</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-foreground-500">%</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { sp: "Tipuana tipu", qty: 184, pct: "18.4%" },
                  { sp: "Ficus benjamina", qty: 142, pct: "14.2%" },
                  { sp: "Lagerstroemia indica", qty: 98, pct: "9.8%" },
                  { sp: "Licania tomentosa", qty: 76, pct: "7.6%" },
                  { sp: "Tabebuia roseoalba", qty: 54, pct: "5.4%" },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-background-50">
                    <td className="px-4 py-2 text-xs text-foreground-700 italic">{row.sp}</td>
                    <td className="px-4 py-2 text-xs text-foreground-900 text-right">{row.qty}</td>
                    <td className="px-4 py-2 text-xs text-foreground-500 text-right">{row.pct}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mapa mockado */}
          <div className="bg-accent-50 rounded-xl border border-accent-200 p-4 text-center">
            <i className="ri-map-2-line text-accent-400 w-10 h-10 flex items-center justify-center mx-auto mb-2" />
            <p className="text-sm text-accent-700 font-medium">Mapa Georreferenciado</p>
            <p className="text-xs text-accent-500 mt-1">Visualização simulada — integração futura com PostGIS</p>
          </div>

          {/* Fotos mockadas */}
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-background-50 rounded-lg aspect-[4/3] border border-background-200 flex items-center justify-center">
                <i className="ri-image-line text-foreground-300 text-2xl w-8 h-8 flex items-center justify-center" />
              </div>
            ))}
          </div>

          {/* Notas técnicas */}
          <div className="bg-secondary-50 rounded-xl border border-secondary-200 p-4">
            <h4 className="text-sm font-semibold text-foreground-900 mb-2">Notas Técnicas</h4>
            <p className="text-xs text-foreground-600 leading-relaxed">
              Este relatório foi gerado a partir dos dados do CTA — Cadastro Técnico Arbóreo. As informações apresentadas refletem o estado do cadastro na data de geração.
              Para dados atualizados, gere uma nova versão do relatório.
            </p>
          </div>
        </div>

        {/* Bottom actions */}
        <div className="sticky bottom-0 bg-white border-t border-background-200 px-6 py-4 flex items-center gap-3">
          <button className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-download-line w-4 h-4 inline-flex items-center justify-center mr-1.5" />
            Baixar PDF
          </button>
          <button className="flex-1 px-4 py-2.5 text-sm font-semibold text-accent-700 bg-accent-50 rounded-lg hover:bg-accent-100 transition-colors whitespace-nowrap">
            <i className="ri-file-excel-line w-4 h-4 inline-flex items-center justify-center mr-1.5" />
            Exportar XLSX
          </button>
          <button className="flex-1 px-4 py-2.5 text-sm font-semibold text-secondary-700 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors whitespace-nowrap">
            <i className="ri-global-line w-4 h-4 inline-flex items-center justify-center mr-1.5" />
            Publicar
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}